import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import languageTranslationStore from '../../../../../store/languageTranslationStore';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import { LoadingStatus } from 'src/types/main';
import { leaveWithoutSaving } from 'src/utils/modalConfirmations';
import Input from 'src/view/components/Controls/Input/Input';
import Text, { TEXT } from 'src/view/components/Text/Text';
import HeaderBack from 'src/view/containers/Headers/components/HeaderBack/HeaderBack';
import Headers from 'src/view/containers/Headers/Headers';
import { LANGUAGE_TRANSLATIONS_TYPE } from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/types/languageTranslations';
import { BUTTON_TITLE } from '~/constants/main';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import CustomSelect from 'src/view/components/Controls/Pickers/CustomSelect/CustomSelect';
import './style.scss';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const UpdateOrCreateLanguageTranslation: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  useEffect(() => {
    if (props.match.params.id) {
      languageTranslationStore.getLanguageTranslation(props.match.params.id);
      return;
    }
    languageTranslationStore.setLanguageTranslation();
  }, []);

  useEffect(() => {
    if (
      !languageTranslationStore.languageTranslation?.id ||
      languageTranslationStore.languageTranslationCreateLoadingStatus !== LoadingStatus.Success
    ) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_TRANSLATION());
  }, [languageTranslationStore.languageTranslationCreateLoadingStatus]);

  useEffect(() => {
    if (
      !languageTranslationStore.languageTranslation?.id ||
      languageTranslationStore.languageTranslation?.id === 0 ||
      languageTranslationStore.languageTranslationUpdateLoadingStatus !== LoadingStatus.Success
    ) {
      return;
    }
    props.history.replace(SCREENS.SETTINGS_TRANSLATION());
  }, [languageTranslationStore.languageTranslationUpdateLoadingStatus]);

  const isLoading = useMemo((): boolean => {
    const loadingStatuses = [LoadingStatus.Loading, LoadingStatus.Init, LoadingStatus.Refreshing];
    return loadingStatuses.includes(languageTranslationStore.languageTranslationLoadingStatus);
  }, [languageTranslationStore.languageTranslationLoadingStatus]);

  const hasErrors = useMemo((): boolean => {
    return !languageTranslationStore.isValid;
  }, [languageTranslationStore.validation]);

  const goBack = () => {
    if (!languageTranslationStore.edited) {
      props.history.push(SCREENS.SETTINGS_TRANSLATION());
      return;
    }
    leaveWithoutSaving({
      title: localeStore.t('title_leave_without_changes'),
      message: localeStore.t('leave_without_changes_desc'),
      onConfirm: () => props.history.push(SCREENS.SETTINGS_TRANSLATION()),
    });
  };

  const onSave = () => {
    if (languageTranslationStore.languageTranslation?.id) {
      languageTranslationStore.updateLanguageTranslation();
      return;
    }
    languageTranslationStore.createLanguageTranslation();
  };

  const getTranslationTypes = () => {
    return Object.entries(LANGUAGE_TRANSLATIONS_TYPE).map(([_, value]) => {
      let type: string = value;
      if (type === LANGUAGE_TRANSLATIONS_TYPE.BACKEND) type = 'backend';

      return {
        label: localeStore.t(`update_${type}_languages`),
        value,
      };
    });
  };
  const selectType = (options: IPickerOption[]) => {
    options.forEach((option) => {
      languageTranslationStore.changeLanguageTranslationHandler({ type: option.value as LANGUAGE_TRANSLATIONS_TYPE });
    });
  };
  const selectedTypes = () => {
    return getTranslationTypes().filter((option) => option.value === languageTranslationStore.languageTranslation?.type);
  };

  return (
    <div className="d-flex flex-column full-width update-or-create-language-translation">
      <Headers>
        <HeaderBack
          title={props.match.params.id ? 'update_translation_page' : 'create_translation_page'}
          callback={goBack}
          path={SCREENS.SETTINGS_TRANSLATION()}
        />
      </Headers>
      <div className="d-flex flex-column flex-grow-1 pt-6">
        {/* translation type */}
        {!languageTranslationStore.languageTranslation?.id && (
          <div className="update-or-create-language-translation__input-wrapper mr-8">
            <Text type={TEXT.CUSTOM} text="language_type" className="update-or-create-language-translation__input-label mr-2" required />
            {!isLoading ? (
              <CustomSelect
                options={getTranslationTypes()}
                onSelect={selectType}
                selectedOptions={selectedTypes()}
                className="update-or-create-language-translation__type-select"
              />
            ) : (
              <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
            )}
          </div>
        )}
        {/* translation key */}
        <div className="update-or-create-language-translation__input-wrapper mr-8">
          <Text type={TEXT.CUSTOM} text="language_key" className="update-or-create-language-translation__input-label mr-2" required />
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-language-translation__input"
              error={!languageTranslationStore.validation.key.state}
              onChange={(e) => languageTranslationStore.changeLanguageTranslationHandler({ key: e.target.value })}
              placeholder={localeStore.t('language_key')}
              value={languageTranslationStore.languageTranslation?.key}
              className="input-mini family-medium text-elipsis"
              errorMessage={languageTranslationStore.validation.key.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>

        {/* translation eng */}
        <div className="update-or-create-language-translation__input-wrapper mr-8">
          <Text type={TEXT.CUSTOM} text="english" className="update-or-create-language-translation__input-label mr-2" />
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-language-translation__input"
              error={!languageTranslationStore.validation.translations.en.state}
              onChange={(e) =>
                languageTranslationStore.changeLanguageTranslationHandler({
                  translations: {
                    en: e.target.value,
                    ar: languageTranslationStore.languageTranslation?.translations.ar || '',
                  },
                })
              }
              placeholder={localeStore.t('english')}
              value={languageTranslationStore.languageTranslation?.translations.en}
              className="input-mini family-medium text-elipsis"
              errorMessage={languageTranslationStore.validation.translations.en.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        {/* translation ar */}
        <div className="update-or-create-language-translation__input-wrapper mr-8">
          <Text type={TEXT.CUSTOM} text="title_arabian" className="update-or-create-language-translation__input-label mr-2" />
          {!isLoading ? (
            <Input
              wrapperClassName="update-or-create-language-translation__input"
              error={!languageTranslationStore.validation.translations.ar.state}
              onChange={(e) =>
                languageTranslationStore.changeLanguageTranslationHandler({
                  translations: {
                    ar: e.target.value,
                    en: languageTranslationStore.languageTranslation?.translations.en || '',
                  },
                })
              }
              placeholder={localeStore.t('title_arabian')}
              value={languageTranslationStore.languageTranslation?.translations.ar}
              className="input-mini family-medium text-elipsis"
              errorMessage={languageTranslationStore.validation.translations.ar.errorText || ''}
            />
          ) : (
            <Skeleton width={240} height={46} style={{ borderRadius: 12 }} />
          )}
        </div>
        <div className="d-flex align-items-center">
          <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={onSave} disabled={hasErrors} loading={isLoading} className="mt-3" />
        </div>
      </div>
    </div>
  );
});

export default UpdateOrCreateLanguageTranslation;
